import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useRedirectToLogin, useGetUserDetailsHooks } from "../../hooks";
import {
	useCreateOpportunityCheckinMutation,
	useGetOpportunityListQuery,
	useLazyGetPreSalesOwnerQuery,
} from "../../../features/api/opportunity-api-slice";
import ColumnGroupingTable from "../../components/table";
import { API_CONSTANTS, APP_CONSTANTS } from "../../constants";
import { OpportunityStatuses, Status } from "../../constants/statuses";
import { useGetProjectsQuery } from "../../../features/api/project-api-slice";
import { ListColumnType } from "../../utils/interface";
import {
	Box,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { StyledButton } from "../opportunity-details/styled-components";
import CheckoutCommentDialog from "../../components/checkout-comment-dialog";
import { getMissingCheckoutFields, showMissingCheckoutFieldsToast } from "../../utils/checkout-validation";
import moment from "moment";

// Constants for localStorage keys
const OPPORTUNITY_FILTERS_KEY = 'opportunityListFilters';

interface CheckIn {
	name: string;
	time: string;
	type: string;
	for_opportunity: string;
	sales_person: string;
}

interface opportunityDataType {
	name: string;
	customer_name: string;
	first_name?: string;
	last_name?: string;
	contact_mobile: string;
	contact_email: string;
	status: Status;
	project: string;
	allocated_sales_team: string;
	follow_up_time?: string;
	modified: string;
	pre_sales_owner: string;
	check_in?: CheckIn;
	sales_stage: string;
	follow_up_date: string;
	location?: string; // Added location field
	employed_at?: string; // Added employed_at field
	budget_range?: string; // Added budget_range field
	custom_rating?: string;
	custom_date?: string;
	source?: string;
	sales_partner?: string;
}

interface opportunityListTableType {
	search?: string;
}

const ERROR = "error";

const OpportunityListTable = ({ search = "" }: opportunityListTableType) => {
	const {
		getUserToken,
		isCurrentUserGRE,
		isCurrentUserCP,
		isProjectHead,
		isProjectSalesHead,
		isProjectSalesUser,
	} = useGetUserDetailsHooks();

	// Helper functions for localStorage
	const loadFiltersFromStorage = () => {
		try {
			const savedFilters = localStorage.getItem(OPPORTUNITY_FILTERS_KEY);
			if (savedFilters) {
				return JSON.parse(savedFilters);
			}
		} catch (error) {
			console.error('Error loading filters from localStorage:', error);
		}
		return {
			selectedStatus: [],
			selectedProjects: [],
			selectedSiteVisitDate: "",
			selectedRating: [],
			selectedFollowUpDate: ""
		};
	};

	const saveFiltersToStorage = (filters: any) => {
		try {
			localStorage.setItem(OPPORTUNITY_FILTERS_KEY, JSON.stringify(filters));
		} catch (error) {
			console.error('Error saving filters to localStorage:', error);
		}
	};

	// Helper functions to get display names for filters
	const getSelectedStatusNames = () => {
		return selectedStatus.map(status => {
			// Convert status to display format if it's an enum value
			if (typeof status === 'string') {
				return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
			}
			return status;
		});
	};

	const getSelectedProjectNames = () => {
		return selectedProjects.map(projectId => {
			const project = (projectList as { name: string }[]).find((p: { name: string }) => p?.name === projectId);
			return project?.name || projectId;
		});
	};

	const { t } = useTranslation();
	const { redirectToLoginBasedOnResponse } = useRedirectToLogin();
	let navigate = useNavigate();
	const [opportunityList, setOpportunityList] = useState([]);
	const [projectList, setProjectList] = useState([]);

	// Initialize state with saved filters
	const savedFilters = loadFiltersFromStorage();
	const [selectedStatus, setSelectedStatus] = useState<string[] | Status[]>(savedFilters.selectedStatus || []);
	const [selectedProjects, setSelectedProjects] = useState<string[]>(savedFilters.selectedProjects || []);
	const [selectedSiteVisitDate, setSelectedSiteVisitDate] = useState<string>(savedFilters.selectedSiteVisitDate || "");
	const [isDateFilterOpen, setIsDateFilterOpen] = useState<boolean>(false);
	const [selectedRating, setSelectedRating] = useState<string[]>(savedFilters.selectedRating || []);
	const [selectedFollowUpDate, setSelectedFollowUpDate] = useState<string>(savedFilters.selectedFollowUpDate || "");
	const [isRatingFilterOpen, setIsRatingFilterOpen] = useState<boolean>(false);
	const [isFollowUpDateFilterOpen, setIsFollowUpDateFilterOpen] = useState<boolean>(false);

	const [searchText, setSearchText] = useState<string>("");
	const [totalOpportunities, setTotalOpportunities] = useState<number>(0);
	const [pagination, setPagination] = useState<{ page: number; size: number }>({
		page: APP_CONSTANTS.defaultPage,
		size: APP_CONSTANTS.defaultPageSize,
	});
	const [checking, setChecking] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [ConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
	const [reportText, setReportText] = useState("");
	const [checkoutRow, setCheckoutRow] = useState<opportunityDataType | null>(null);
	const [createOpportunityCheckin, { isLoading }] =
		useCreateOpportunityCheckinMutation();
	const [CheckInStatus, setCheckInStatus] = useState<string | null>(null);
	const [canCheckIn, setCanCheckIn] = useState(true); // calculated from backend
	const [remainingHours, setRemainingHours] = useState<string>("0h 0m 0s");
	const [owner, setOwner] = useState<opportunityDataType[]>([]);
	const [checkInData, setCheckInData] = useState<{
		opportunityId: string;
		assignedTo: string;
	} | null>(null);

	const [getPreSalesOwner, { isLoading: isGetPreSalesOwnerLoading }] =
		useLazyGetPreSalesOwnerQuery();

	const {
		data: getOpportunityListResponse,
		isError: isGetOpportunityListFailed,
		isSuccess: isGetOpportunityListSuccess,
		error: getOpportunityListError,
		refetch: refetchOpportunity,
		isFetching: isOpportunityListFetching,
	} = useGetOpportunityListQuery(
		{
			paginationInfo: pagination,
			selectedStatus,
			selectedProjects,
			searchText,
			search,
			selectedSiteVisitDate,
			selectedRating,
			selectedFollowUpDate,
			userToken: getUserToken(),
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const {
		data: getProjectListResponse,
		isSuccess: isGetProjectListSuccess,
		isError: isGetProjectListFailed,
	} = useGetProjectsQuery({});

	// Add useEffect to refetch data when component mounts (page visit)
	useEffect(() => {
		refetchOpportunity();
		getPreSalesOwnerData();
	}, []); // Empty dependency array means it runs only on mount

	// Save filters to localStorage whenever they change
	useEffect(() => {
		const filters = {
			selectedStatus,
			selectedProjects,
			selectedSiteVisitDate,
			selectedRating,
			selectedFollowUpDate
		};
		saveFiltersToStorage(filters);
	}, [selectedStatus, selectedProjects, selectedSiteVisitDate]);

	const getPreSalesOwnerData = async () => {
		try {
			const response = await getPreSalesOwner({
				userToken: getUserToken(),
			}).unwrap();
			const responseData = response?.data;
			setOwner(responseData);
		} catch (error) {
			console.error("Error fetching pre-sales owner:", error);
		}
	};

	const createRowData = (data: opportunityDataType[]) => {
		let newData = data.map((item: opportunityDataType) => {
			let checkInStatusText = "Not Checked In"; // default for "Not Checked In"
			if (item?.check_in?.type === "In") {
				checkInStatusText = "Checked In";
			}

			const shouldHideAction =
				item.sales_stage === "Confirmed" ||
				item.sales_stage === "Booking Confirmed" ||
				item.sales_stage === "Client Not Visited" ||
				item.sales_stage === "Allotment Complete" ||
				item.sales_stage === "Agreement Completed";
			// Determine if user is allowed to check in
			let allowCheckIn = false;
			let allowCheckOut = false;

			// console.log(item);
			if (!item?.check_in) {
				// allowCheckIn = true; // New user, no history
				const today = moment().startOf("day");
				const followUpDate = moment(item.follow_up_date).startOf("day");
				allowCheckIn = followUpDate.isSame(today);
			} else if (item.check_in.type === "In") {
				allowCheckOut = true; // Already checked in
			} else if (item.check_in.type === "Out") {
				const logoutTime = moment(item.check_in.time);
				const now = moment();
				const diffInHours = now.diff(logoutTime, "hours", true);
				// allowCheckIn = diffInHours >= 12;
				allowCheckIn = diffInHours >= 3; // for three hours
			}

			const clickableCheckInStatus =
				!shouldHideAction &&
					(isProjectSalesHead || isProjectHead || isProjectSalesUser) ? (
					allowCheckOut || allowCheckIn ? (
						<StyledButton
							style={{
								cursor: "pointer",
								border:
									checkInStatusText === "Checked In" ? "#98fb98" : "#ffa07a",
								background:
									checkInStatusText === "Checked In" ? "#ffa07a" : "#98fb98",
								textShadow: "1px 1px 3px rgba(0, 0, 0, 0.1)",
								padding: "0px 10px",
								width: "90%",
							}}
							onClick={(e) => {
								e.stopPropagation();
								// Pass the entire item to handleCheckInClick
								handleCheckInClick(item, checkInStatusText);
								if (!item?.check_in) {
									setCanCheckIn(true);
									setRemainingHours("0h 0m 0s");
									return;
								}
								if (item.check_in.type === "Out") {
									const logoutTime = moment(item.check_in.time);
									const now = moment();
									const diffInHours = now.diff(logoutTime, "hours", true);

									if (diffInHours >= 3) {
										setCanCheckIn(true);
										setRemainingHours("0h 0m 0s");
									} else {
										setCanCheckIn(false);
										const remainingMs = (3 - diffInHours) * 60 * 60 * 1000;
										const duration = moment.duration(remainingMs);
										const hrs = Math.floor(duration.asHours());
										const mins = duration.minutes();
										const secs = duration.seconds();
										const timeString = `${hrs}h ${mins}m ${secs}s left`;
										setRemainingHours(timeString);
									}
								} else if (item.check_in.type === "In") {
									setCanCheckIn(false);
								}
							}}
						>
							{checkInStatusText === "Checked In" ? "Check Out" : "Check In"}
						</StyledButton>
					) : (
						<span></span>
					)
				) : (
					<span></span>
				);

			return {
				id: item.name,
				name: item?.first_name
					? `${item?.first_name} ${item?.last_name ? item?.last_name : ""}`
					: "",
				phoneNumber: item?.contact_mobile
					? `xxxxxx${item.contact_mobile.slice(-4)}`
					: "",
				email: item?.contact_email,
				status: isCurrentUserCP
					? (item?.status as Status)
					: (item?.sales_stage as Status),
				project: item?.project,
				assignedTo: item?.allocated_sales_team,
				modified: item?.modified,
				SiteVisitDate:
					item?.follow_up_date && item?.follow_up_time
						? moment(
							`${item.follow_up_date} ${item.follow_up_time}`,
							"YYYY-MM-DD HH:mm:ss"
						).format("DD-MM-YYYY hh:mm A")
						: "",
				rating: item?.custom_rating || "",
				followUpDate: item?.custom_date
					? moment(item.custom_date).format("DD-MM-YYYY")
					: "",
				checkInStatus: clickableCheckInStatus, // Use clickable component
			};
		});
		return newData;
	};

	const projectName = useMemo(
		() => projectList?.map((project: { name: string }) => project?.name),
		[projectList]
	);

	const preSalesOwners = useMemo(
		() => owner?.map((ownerObj) => ownerObj.pre_sales_owner),
		[owner]
	);

	const columns: ListColumnType[] = [
		{
			id: "name",
			label: t("opportunity.customer_name"),
			align: "center",
		},
		{
			id: "phoneNumber",
			label: t("opportunity.phone_number"),
			align: "center",
		},
		{
			id: "status",
			label: t("opportunity.status"),
			align: "center",
			isMultiSelectionEnabled: true,
			isMultiSelectionDropdownOpen: false,
			values: OpportunityStatuses,
		},
		{
			id: "project",
			label: t("opportunity.project"),
			align: "center",
			isMultiSelectionEnabled: true,
			isMultiSelectionDropdownOpen: false,
			values: projectName,
		},
		{
			id: "SiteVisitDate",
			label: "Site Visit Date",
			align: "center",
			isMultiSelectionEnabled: true, // Still need this to trigger the custom handler
			isMultiSelectionDropdownOpen: false,
			values: [], // Empty array - handled by custom click handler
		},
		{
			id: "rating",
			label: "Rating",
			align: "center",
			isMultiSelectionEnabled: true,
			isMultiSelectionDropdownOpen: false,
			values: ["Hot", "Warm", "Cold"],
		},
		{
			id: "followUpDate",
			label: "Follow Up Date",
			align: "center",
			isMultiSelectionEnabled: true, // Still need this to trigger the custom handler
			isMultiSelectionDropdownOpen: false,
			values: [], // Empty array - handled by custom click handler
		},
		{
			id: "assignedTo",
			label: t("opportunity.assignedTo"),
			align: "center",
		},
		// ✅ Include only if NOT a CP user
		...(!isCurrentUserCP && !isCurrentUserGRE
			? [
				{
					id: "checkInStatus",
					label: t("opportunity.Action"),
					align: "center",
				} as ListColumnType,
			]
			: []),
	];

	const handleOpportunityListResponse = useCallback(() => {
		if (isGetOpportunityListSuccess) {
			if (
				getOpportunityListResponse?.data &&
				getOpportunityListResponse?.status_code ===
				API_CONSTANTS.successStatusCode
			) {
				setOpportunityList(getOpportunityListResponse.data.opportunities);
				setTotalOpportunities(
					getOpportunityListResponse.data.total_opportunities
				);
			} else if (getOpportunityListResponse?.message) {
				toast(getOpportunityListResponse.message, { type: ERROR });
				setOpportunityList([]);
			}
		} else if (isGetOpportunityListFailed) {
			redirectToLoginBasedOnResponse(
				getOpportunityListError,
				getOpportunityListResponse
			);
			setOpportunityList([]);
		}
	}, [
		isGetOpportunityListSuccess,
		isGetOpportunityListFailed,
		getOpportunityListResponse,
		getOpportunityListError,
		redirectToLoginBasedOnResponse,
	]);

	// Single useEffect for handling opportunity list response
	useEffect(() => {
		handleOpportunityListResponse();
	}, [handleOpportunityListResponse]);

	useEffect(() => {
		if (isGetProjectListSuccess) {
			if (getProjectListResponse?.message?.length) {
				setProjectList(getProjectListResponse?.message);
			} else if (!Array.isArray(getProjectListResponse?.message)) {
				toast(getProjectListResponse?.message, {
					type: ERROR,
				});
				setProjectList([]);
			}
		} else if (isGetProjectListFailed) {
			toast(t("error_messages.server_error"), {
				type: ERROR,
			});
			setProjectList([]);
		}
	}, [
		getProjectListResponse,
		isGetProjectListSuccess,
		isGetProjectListFailed,
		t,
	]);

	useEffect(() => {
		setSearchText(search);
		setPagination({
			page: APP_CONSTANTS.defaultPage,
			size: APP_CONSTANTS.defaultPageSize,
		});
	}, [search]);

	// Refetch when filters change
	useEffect(() => {
		refetchOpportunity();
	}, [
		selectedStatus,
		selectedProjects,
		searchText,
		selectedSiteVisitDate,
		selectedRating,
		selectedFollowUpDate,
		pagination,
		refetchOpportunity,
	]);

	const onUpdateValues = useCallback(
		(values: string[], label: string) => {
			if (label === t("opportunity.status")) {
				setSelectedStatus(values);
			} else if (label === t("opportunity.project")) {
				setSelectedProjects(values);
			} else if (label === "Rating") {
				setSelectedRating(values);
			}
			// Reset pagination when filters change
			setPagination({
				page: APP_CONSTANTS.defaultPage,
				size: APP_CONSTANTS.defaultPageSize,
			});
		},
		[t]
	);

	const onChangeSiteVisitDate = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSelectedSiteVisitDate(e.target.value);
			// Reset pagination when filters change
			setPagination({
				page: APP_CONSTANTS.defaultPage,
				size: APP_CONSTANTS.defaultPageSize,
			});
		},
		[]
	);

	const handlePageChange = (page: number, size: number) => {
		setPagination({ page, size });
	};

	const navigateToOpportunityDetails = useCallback(
		(id: string) => navigate(`/opportunity/${id}`),
		[navigate]
	);

	// Handle check-in status click
	const handleCheckInClick = (
		opportunity: opportunityDataType,
		currentStatus: string
	) => {
		setCheckInStatus(currentStatus);

		if (currentStatus === "Checked In") {
			const missing = getMissingCheckoutFields(opportunity);
			if (missing.length > 0) {
				showMissingCheckoutFieldsToast(missing, opportunity.name, () =>
					navigate(`/opportunity/${opportunity.name}/edit`, { state: { openCheckout: true } })
				);
				return;
			}
			// one comment box is shared by every row, so only keep the text for the same opportunity
			if (checkInData?.opportunityId !== opportunity.name) {
				setReportText("");
			}
			setCheckInData({
				opportunityId: opportunity.name,
				assignedTo: opportunity.allocated_sales_team,
			});
			setCheckoutRow(opportunity);
			setDialogOpen(true);
		} else if (currentStatus === "Not Checked In") {
			setCheckInData({
				opportunityId: opportunity.name,
				assignedTo: opportunity.allocated_sales_team,
			});
			setConfirmationDialogOpen(true);
		}
	};

	const handleSubmit = async () => {
		if (!checkInData) {
			console.warn("No check-in data available");
			return;
		}
		setChecking(true);
		const payload = {
			for_opportunity: checkInData?.opportunityId,
			sales_person: checkInData?.assignedTo,
			checkin_type: CheckInStatus === "Checked In" ? "Out" : "In",
			comment: reportText,
		};

		try {
			const res = await createOpportunityCheckin({
				payload,
				userToken: getUserToken(),
			}).unwrap();
			if (res.status_code === 200) {
				toast.success(res.message);
				setReportText("");
				setDialogOpen(false);
				setConfirmationDialogOpen(false);

				// Force refetch the opportunity list to get fresh data
				refetchOpportunity();
			} else {
				toast.error(res.message || "Something went wrong. Please try again.");
			}
		} catch (error) {
			console.error("Error creating check-in:", error);
			toast(t("error_messages.server_error"), { type: ERROR });
		} finally {
			setChecking(false);
			// console.log("Payload for check-in:", payload, CheckInStatus);
		}
	};


	return (
		<>
			{/* Active Filters Indicator */}
			{(selectedSiteVisitDate || selectedStatus.length > 0 || selectedProjects.length > 0 || selectedRating.length > 0 || selectedFollowUpDate) && (
				<Box
					sx={{
						mb: 2,
						p: 2,
						backgroundColor: '#e3f2fd',
						borderRadius: 1,
						border: '1px solid #2196f3',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						flexWrap: 'wrap',
						gap: 1
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
						<Typography
							sx={{
								fontSize: 14,
								color: '#1976d2',
								fontFamily: 'Lato-Bold',
								fontWeight: 600
							}}
						>
							Active Filters:
						</Typography>

						{selectedSiteVisitDate && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography
									sx={{
										fontSize: 12,
										color: '#1976d2',
										fontFamily: 'Lato-Regular',
										backgroundColor: '#bbdefb',
										padding: '4px 8px',
										borderRadius: '12px'
									}}
								>
									🗓️ Date: {moment(selectedSiteVisitDate).format('DD MMM YYYY')}
								</Typography>
								<StyledButton
									onClick={() => {
										setSelectedSiteVisitDate("");
										setPagination({
											page: APP_CONSTANTS.defaultPage,
											size: APP_CONSTANTS.defaultPageSize,
										});
									}}
									size="small"
									style={{
										background: "transparent",
										color: "#d32f2f",
										border: "none",
										padding: "0",
										fontSize: "14px",
										minWidth: "auto",
										cursor: "pointer"
									}}
								>
									✕
								</StyledButton>
							</Box>
						)}

						{selectedStatus.length > 0 && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography
									sx={{
										fontSize: 12,
										color: '#1976d2',
										fontFamily: 'Lato-Regular',
										backgroundColor: '#bbdefb',
										padding: '4px 8px',
										borderRadius: '12px',
										maxWidth: '200px',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									}}
									title={getSelectedStatusNames().join(', ')}
								>
									📊 Status: {getSelectedStatusNames().length <= 2
										? getSelectedStatusNames().join(', ')
										: `${getSelectedStatusNames().slice(0, 2).join(', ')} +${getSelectedStatusNames().length - 2} more`
									}
								</Typography>
								<StyledButton
									onClick={() => {
										setSelectedStatus([]);
										setPagination({
											page: APP_CONSTANTS.defaultPage,
											size: APP_CONSTANTS.defaultPageSize,
										});
									}}
									size="small"
									style={{
										background: "transparent",
										color: "#d32f2f",
										border: "none",
										padding: "0",
										fontSize: "14px",
										minWidth: "auto",
										cursor: "pointer"
									}}
								>
									✕
								</StyledButton>
							</Box>
						)}

						{selectedProjects.length > 0 && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography
									sx={{
										fontSize: 12,
										color: '#1976d2',
										fontFamily: 'Lato-Regular',
										backgroundColor: '#bbdefb',
										padding: '4px 8px',
										borderRadius: '12px',
										maxWidth: '200px',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									}}
									title={getSelectedProjectNames().join(', ')}
								>
									🏢 Projects: {getSelectedProjectNames().length <= 2
										? getSelectedProjectNames().join(', ')
										: `${getSelectedProjectNames().slice(0, 2).join(', ')} +${getSelectedProjectNames().length - 2} more`
									}
								</Typography>
								<StyledButton
									onClick={() => {
										setSelectedProjects([]);
										setPagination({
											page: APP_CONSTANTS.defaultPage,
											size: APP_CONSTANTS.defaultPageSize,
										});
									}}
									size="small"
									style={{
										background: "transparent",
										color: "#d32f2f",
										border: "none",
										padding: "0",
										fontSize: "14px",
										minWidth: "auto",
										cursor: "pointer"
									}}
								>
									✕
								</StyledButton>
							</Box>
						)}

						{selectedRating.length > 0 && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography
									sx={{
										fontSize: 12,
										color: '#1976d2',
										fontFamily: 'Lato-Regular',
										backgroundColor: '#bbdefb',
										padding: '4px 8px',
										borderRadius: '12px',
										maxWidth: '200px',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									}}
									title={selectedRating.join(', ')}
								>
									⭐ Rating: {selectedRating.length <= 2
										? selectedRating.join(', ')
										: `${selectedRating.slice(0, 2).join(', ')} +${selectedRating.length - 2} more`
									}
								</Typography>
								<StyledButton
									onClick={() => {
										setSelectedRating([]);
										setPagination({
											page: APP_CONSTANTS.defaultPage,
											size: APP_CONSTANTS.defaultPageSize,
										});
									}}
									size="small"
									style={{
										background: "transparent",
										color: "#d32f2f",
										border: "none",
										padding: "0",
										fontSize: "14px",
										minWidth: "auto",
										cursor: "pointer"
									}}
								>
									✕
								</StyledButton>
							</Box>
						)}

						{selectedFollowUpDate && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography
									sx={{
										fontSize: 12,
										color: '#1976d2',
										fontFamily: 'Lato-Regular',
										backgroundColor: '#bbdefb',
										padding: '4px 8px',
										borderRadius: '12px'
									}}
								>
									📅 Follow Up: {moment(selectedFollowUpDate).format('DD MMM YYYY')}
								</Typography>
								<StyledButton
									onClick={() => {
										setSelectedFollowUpDate("");
										setPagination({
											page: APP_CONSTANTS.defaultPage,
											size: APP_CONSTANTS.defaultPageSize,
										});
									}}
									size="small"
									style={{
										background: "transparent",
										color: "#d32f2f",
										border: "none",
										padding: "0",
										fontSize: "14px",
										minWidth: "auto",
										cursor: "pointer"
									}}
								>
									✕
								</StyledButton>
							</Box>
						)}
					</Box>

					<StyledButton
						onClick={() => {
							setSelectedSiteVisitDate("");
							setSelectedStatus([]);
							setSelectedProjects([]);
							setSelectedRating([]);
							setSelectedFollowUpDate("");
							setPagination({
								page: APP_CONSTANTS.defaultPage,
								size: APP_CONSTANTS.defaultPageSize,
							});
						}}
						size="small"
						style={{
							background: "#ff6b6b",
							color: "white",
							border: "1px solid #ff6b6b",
							padding: "6px 12px",
							fontSize: "12px",
							minWidth: "auto"
						}}
					>
						Clear All Filters
					</StyledButton>
				</Box>
			)}
			<ColumnGroupingTable
				token={getUserToken()}
				rows={createRowData(opportunityList)}
				isCurrentUserCP={isCurrentUserCP}
				columns={columns}
				onUpdateValue={onUpdateValues}
				onCustomColumnHeaderClick={(columnLabel) => {
					if (columnLabel === "Site Visit Date") {
						setIsDateFilterOpen(true);
					} else if (columnLabel === "Follow Up Date") {
						setIsFollowUpDateFilterOpen(true);
					}
				}}
				handlePageChange={handlePageChange}
				paginationData={pagination}
				count={totalOpportunities}
				onClickTableRow={navigateToOpportunityDetails}
				loading={isOpportunityListFetching} // Add loading state if your table component supports it
			/>
			<ToastContainer />

			{/* Date Filter Dialog */}
			<Dialog
				open={isDateFilterOpen}
				onClose={() => setIsDateFilterOpen(false)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle sx={{ fontFamily: "Lora-Bold" }}>
					Filter by Site Visit Date
				</DialogTitle>
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
						<LocalizationProvider dateAdapter={AdapterMoment}>
							<DatePicker
								label="Select Site Visit Date"
								value={selectedSiteVisitDate ? moment(selectedSiteVisitDate) : null}
								onChange={(newValue) => {
									if (newValue) {
										setSelectedSiteVisitDate(newValue.format('YYYY-MM-DD'));
									} else {
										setSelectedSiteVisitDate('');
									}
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										fullWidth
										sx={{
											'& .MuiInputBase-root': {
												fontFamily: 'Lora-Regular',
												fontSize: 16,
											}
										}}
									/>
								)}
								views={['year', 'month', 'day']}
								inputFormat="DD/MM/YYYY"
								openTo="day"
							/>
						</LocalizationProvider>
						{selectedSiteVisitDate && (
							<Typography variant="body2" color="primary">
								Filtering by: {moment(selectedSiteVisitDate).format('DD MMM YYYY')}
							</Typography>
						)}
					</Box>
				</DialogContent>
				<DialogActions sx={{ marginRight: '18px' }}>
					<StyledButton
						onClick={() => {
							setSelectedSiteVisitDate("");
							setIsDateFilterOpen(false);
							setPagination({
								page: APP_CONSTANTS.defaultPage,
								size: APP_CONSTANTS.defaultPageSize,
							});
						}}
						color="secondary"
					>
						Clear Filter
					</StyledButton>
					<StyledButton
						onClick={() => setIsDateFilterOpen(false)}
						variant="contained"
						style={{
							background: "#04AA6D",
							color: "white",
							border: "1px solid #04AA6D"
						}}
					>
						Apply Filter
					</StyledButton>
				</DialogActions>
			</Dialog>

			{/* Follow Up Date Filter Dialog */}
			<Dialog
				open={isFollowUpDateFilterOpen}
				onClose={() => setIsFollowUpDateFilterOpen(false)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle sx={{ fontFamily: "Lora-Bold" }}>
					Filter by Follow Up Date
				</DialogTitle>
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
						<LocalizationProvider dateAdapter={AdapterMoment}>
							<DatePicker
								label="Select Follow Up Date"
								value={selectedFollowUpDate ? moment(selectedFollowUpDate) : null}
								onChange={(newValue) => {
									if (newValue) {
										setSelectedFollowUpDate(newValue.format('YYYY-MM-DD'));
									} else {
										setSelectedFollowUpDate('');
									}
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										fullWidth
										sx={{
											'& .MuiInputBase-root': {
												fontFamily: 'Lora-Regular',
												fontSize: 16,
											}
										}}
									/>
								)}
								views={['year', 'month', 'day']}
								inputFormat="DD/MM/YYYY"
								openTo="day"
							/>
						</LocalizationProvider>
						{selectedFollowUpDate && (
							<Typography variant="body2" color="primary">
								Filtering by: {moment(selectedFollowUpDate).format('DD MMM YYYY')}
							</Typography>
						)}
					</Box>
				</DialogContent>
				<DialogActions sx={{ marginRight: '18px' }}>
					<StyledButton
						onClick={() => {
							setSelectedFollowUpDate("");
							setIsFollowUpDateFilterOpen(false);
							setPagination({
								page: APP_CONSTANTS.defaultPage,
								size: APP_CONSTANTS.defaultPageSize,
							});
						}}
						color="secondary"
					>
						Clear Filter
					</StyledButton>
					<StyledButton
						onClick={() => setIsFollowUpDateFilterOpen(false)}
						variant="contained"
						style={{
							background: "#04AA6D",
							color: "white",
							border: "1px solid #04AA6D"
						}}
					>
						Apply Filter
					</StyledButton>
				</DialogActions>
			</Dialog>

			<CheckoutCommentDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				comment={reportText}
				onCommentChange={setReportText}
				onSubmit={handleSubmit}
				submitting={checking}
				details={{
					customerName: checkoutRow?.customer_name,
					project: checkoutRow?.project,
					opportunityId: checkoutRow?.name,
					source: checkoutRow?.source,
					cpName: checkoutRow?.sales_partner,
					checkInTime: checkoutRow?.check_in?.time,
				}}
			/>

			<Dialog
				open={ConfirmationDialogOpen}
				onClose={() => setConfirmationDialogOpen(false)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle
					sx={{ fontFamily: "Lora-Bold", fontSize: 24, textAlign: "center" }}
				>
					Check In
				</DialogTitle>

				<DialogContent
					sx={{
						fontFamily: "Lora-Regular",
						fontSize: 16,
						color: "#444",
						textAlign: "center",
						py: 3,
					}}
				>
					{/* Kindly confirm your check-in so we can keep track of your site visit. */}
					{canCheckIn
						? "Kindly confirm your check-in so we can keep track of your site visit."
						: `⏳ You cannot check in now. Please wait another ${remainingHours} hour(s).`}
				</DialogContent>

				<DialogActions sx={{ marginRight: "18px" }}>
					<StyledButton
						onClick={() => setConfirmationDialogOpen(false)}
						color="secondary"
						sx={{ minWidth: 120 }}
					>
						Cancel
					</StyledButton>

					<StyledButton
						onClick={handleSubmit}
						variant="contained"
						disabled={!canCheckIn}
						style={{
							backgroundColor: canCheckIn ? "#04AA6D" : "#d3d3d3",
							color: "white",
							border: canCheckIn ? "1px solid #04AA6D" : "none",
							minWidth: 120,
						}}
					>
						{checking ? (
							<>
								Confirming{" "}
								<CircularProgress color="inherit" size={20} sx={{ ml: 1 }} />
							</>
						) : (
							"Check In"
						)}
					</StyledButton>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default OpportunityListTable;
